import type { Dispatch } from 'react';
import type { Activity, Health, HomeTheme, PhotoKind, Privacy, PulseItem, TimelineUpdate } from '../domain/types';
import type { Action, AppState, SheetState, UiState } from './reducer';
import { ApiError, api, apiActive } from '../services/api';
import { canTickTask, getSession } from '../services/auth';
import { createSeed } from '../data/seed';
import { isDemo, nowIso } from '../lib/clock';
import { roleOf } from './selectors';

/** Edit profile changes; absent sections are unchanged. */
export interface ProfileEdit {
  bio?: string;
  affiliations?: { list: string[]; primary: string };
  unit?: { unitId: string; referralUrl: string };
}
export type SaveResult = { ok: true } | { ok: false; field?: string; code: string };

let seq = 0;
const uid = (p: string): string => `${p}-${Date.now().toString(36)}-${(seq++).toString(36)}`;
const first = (name: string): string => name.split(' ')[0];

export function createActions(dispatch: Dispatch<Action>, get: () => AppState, reload: () => Promise<boolean> = async () => false) {
  const toast = (text: string): void => dispatch({ type: 'ui/toast', id: Date.now() + seq++, text });
  /**
   * Local state is already updated (optimistic). Api mode: when the server answers,
   * re-read GET /me/bootstrap so the screen shows exactly what was saved (server ids,
   * server rules). On failure the reload rolls the optimistic change back.
   */
  const call = (p: Promise<unknown>): void => {
    p.then(
      () => (apiActive ? void reload() : undefined),
      (err: unknown) => {
        if (!apiActive) return;
        if (err instanceof ApiError && err.status === 401) return; // the gate shows "session ended"
        toast(err instanceof ApiError && err.status === 403 ? 'Not allowed. Nothing was changed.' : 'Could not save. Nothing was changed.');
        void reload();
      },
    );
  };
  const d = () => get().data;
  const pulseItem = (activityId: string, statement: string, kind: PulseItem['kind'], extra: Partial<PulseItem> = {}): PulseItem => ({
    id: uid('pulse'), authorId: d().me, activityId, statement, kind, visibility: 'network', createdAt: nowIso(), comments: [], ...extra,
  });

  return {
    toast,
    openSheet: (sheet: SheetState) => dispatch({ type: 'ui/sheet', sheet }),
    closeSheet: () => dispatch({ type: 'ui/sheet', sheet: null }),
    setPulseFilter: (filter: string) => dispatch({ type: 'ui/pulseFilter', filter }),
    setDiscover: (patch: Partial<UiState['discover']>) => dispatch({ type: 'ui/discover', patch }),
    setCalendar: (patch: Partial<UiState['calendar']>) => dispatch({ type: 'ui/calendar', patch }),

    toggleTask(taskId: string, opts: { quiet?: boolean } = {}) {
      const t = d().tasks.find((x) => x.id === taskId);
      if (!t) return;
      if (!canTickTask(getSession(), t)) {
        toast(`${first(d().ambassadors[t.ownerId]?.name ?? 'A teammate')} owns this task`);
        return;
      }
      dispatch({ type: 'task/toggle', taskId });
      call(api.setTaskDone(taskId, !t.done));
      if (!t.done && !opts.quiet) toast('Done · team notified');
    },

    setHealth(activityId: string, health: Health) {
      dispatch({ type: 'activity/health', activityId, health });
      call(api.setHealth(activityId, health));
    },

    /** Update progress sheet. postToPulse=false keeps the update team-only. */
    publishProgress(input: { activityId: string; note: string; mediaCount: number; postToPulse: boolean; notifyTeam: boolean; doneTitles: string[] }) {
      const a = d().activities[input.activityId];
      const health = a.health;
      const text = input.note.trim() || (input.doneTitles.length ? `Done: ${input.doneTitles.join(', ').toLowerCase()}.` : `Status: ${health.toLowerCase()}.`);
      const update: TimelineUpdate = {
        id: uid('upd'), activityId: a.id, authorId: d().me, text, health, mediaCount: input.mediaCount,
        visibility: input.postToPulse ? 'network' : 'team', createdAt: nowIso(),
      };
      const pulse = input.postToPulse ? pulseItem(a.id, `Updated ${a.name}: ${text}`, health === 'On track' ? 'update' : 'help') : undefined;
      dispatch({ type: 'update/add', update, pulse });
      dispatch({ type: 'ui/pulseFilter', filter: 'All' });
      call(api.publishUpdate(update, { postToPulse: input.postToPulse, notifyTeam: input.notifyTeam }));
      toast(input.postToPulse ? 'Published · top of the Pulse' : 'Saved for the team · not on the Pulse');
    },

    /** Create → Post update. */
    postUpdate(input: { activityId: string; text: string; mediaCount: number; visibility: 'network' | 'team' }) {
      const a = d().activities[input.activityId];
      const update: TimelineUpdate = {
        id: uid('upd'), activityId: a.id, authorId: d().me, text: input.text.trim(), mediaCount: input.mediaCount, visibility: input.visibility, createdAt: nowIso(),
      };
      const pulse = pulseItem(a.id, input.text.trim(), 'update', { visibility: input.visibility, photo: input.mediaCount ? a.photo : undefined });
      dispatch({ type: 'update/add', update, pulse });
      dispatch({ type: 'ui/pulseFilter', filter: 'All' });
      call(api.publishUpdate(update, { postToPulse: input.visibility === 'network', notifyTeam: true }));
      toast(input.visibility === 'network' ? 'Published · top of the Pulse' : 'Posted to your team');
    },

    /** Resolves to the new activity id (the server's id in api mode), or null if saving failed. */
    async proposeActivity(input: Omit<Activity, 'id' | 'stage' | 'organiserId' | 'team' | 'health'>, announce: boolean): Promise<string | null> {
      const me = d().me;
      const activity: Activity = { ...input, id: uid('act'), stage: 0, organiserId: me, team: [{ ambassadorId: me, role: 'Lead organiser (proposed)' }], health: 'On track' };
      if (apiActive) {
        try {
          const id = await api.proposeActivity(activity, { announce });
          await reload();
          toast('Proposal sent · awaiting approval');
          return id;
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 401)) toast('Could not send the proposal. Please check the details and try again.');
          return null;
        }
      }
      const update: TimelineUpdate = { id: uid('upd'), activityId: activity.id, authorId: me, text: 'Proposal submitted for approval.', mediaCount: 0, visibility: 'team', createdAt: nowIso() };
      const pulse = announce ? pulseItem(activity.id, `Proposed a new activity: ${activity.name}.`, activity.needs.length ? 'help' : 'proposal') : undefined;
      dispatch({ type: 'activity/propose', activity, update, pulse });
      toast('Proposal sent · awaiting approval');
      return activity.id;
    },

    /** M+E approver decision (server-enforced: approvers only, never on their own proposal). */
    async decideProposal(activityId: string, decision: 'approve' | 'reject', reason: string): Promise<boolean> {
      if (!apiActive) {
        toast('Approvals need the UGAIN server (not available in the demo).');
        return false;
      }
      try {
        await api.decideProposal(activityId, decision, reason);
        await reload();
        toast(decision === 'approve' ? 'Approved · the activity is now in Planning' : 'Proposal declined · the organiser sees your reason');
        return true;
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) toast(err instanceof ApiError && err.status === 403 ? 'You cannot review this proposal.' : 'Could not save the decision.');
        return false;
      }
    },

    /** Lead (or approver) assigns a task to a team member. */
    async addTask(activityId: string, task: { title: string; ownerId: string; due: string }): Promise<boolean> {
      if (!apiActive) {
        dispatch({ type: 'task/add', task: { id: uid('task'), activityId, done: false, ...task } });
        toast('Task added');
        return true;
      }
      try {
        await api.addTask(activityId, task);
        await reload();
        toast('Task added');
        return true;
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) toast(err instanceof ApiError && err.status === 403 ? 'Only the lead organiser can add tasks.' : 'Could not add the task.');
        return false;
      }
    },

    requestHelp(activityId: string, roles: string[], details: string) {
      if (!roles.length) return;
      const a = d().activities[activityId];
      const statement = `Looking for ${roles.join(', ').toLowerCase()} for ${a.name}.${details.trim() ? ` ${details.trim()}` : ''}`;
      const pulse = pulseItem(activityId, statement, 'help', { helpKey: roles.length === 1 ? `${activityId}|${roles[0]}` : undefined });
      dispatch({ type: 'help/post', activityId, roles, pulse });
      call(api.postHelpRequest(activityId, roles, details));
      toast('Posted to the Pulse as Help wanted');
    },

    toggleHelpRole(activityId: string, role: string) {
      const key = `${activityId}|${role}`;
      if (d().helpPosted[key]) {
        dispatch({ type: 'help/withdraw', activityId, role });
        call(api.withdrawHelpRequest(activityId, role));
        return;
      }
      const a = d().activities[activityId];
      dispatch({ type: 'help/post', activityId, roles: [role], pulse: pulseItem(activityId, `Looking for ${role.toLowerCase()} for ${a.name}.`, 'help', { helpKey: key }) });
      call(api.postHelpRequest(activityId, [role], ''));
      toast('Posted to the Pulse as Help wanted');
    },

    toggleFollowActivity(activityId: string) {
      const on = !d().followedActivities[activityId];
      dispatch({ type: 'follow/activity', activityId });
      call(api.setFollow('activity', activityId, on));
      if (on) toast(`Following ${d().activities[activityId].name}`);
    },

    toggleFollowPerson(personId: string) {
      const on = !d().followedPeople[personId];
      dispatch({ type: 'follow/person', personId });
      call(api.setFollow('ambassador', personId, on));
    },

    toggleOffer(activityId: string) {
      const on = !d().offeredHelp[activityId];
      dispatch({ type: 'offer/toggle', activityId });
      call(api.setOfferHelp(activityId, on));
      if (on) toast(`${first(d().ambassadors[d().activities[activityId].organiserId].name)} will see your offer`);
    },

    acceptOffer(offerId: string) {
      const o = d().offers.find((x) => x.id === offerId);
      if (!o) return;
      const name = first(d().ambassadors[o.ambassadorId].name);
      dispatch({ type: 'offer/accept', offerId, message: { id: uid('msg'), activityId: o.activityId, authorId: d().me, text: `Welcome ${name}! You are on ${o.role.toLowerCase()} duty.`, createdAt: nowIso() } });
      call(api.acceptOffer(offerId));
      toast(`${name} joined the team`);
    },

    sendMessage(activityId: string, text: string) {
      const t = text.trim();
      if (!t || roleOf(d(), activityId) === null) return;
      const message = { id: uid('msg'), activityId, authorId: d().me, text: t, createdAt: nowIso() };
      dispatch({ type: 'chat/send', message });
      call(api.sendMessage(message));
    },

    comment(pulseId: string, text: string) {
      const t = text.trim();
      if (!t) return;
      const comment = { id: uid('cmt'), authorId: d().me, text: t, createdAt: nowIso() };
      dispatch({ type: 'pulse/comment', pulseId, comment });
      call(api.addComment(pulseId, comment));
    },

    recordShare(kitId: string, platform: string, url?: string) {
      const record = { id: uid('shr'), kitId, platform, url, at: nowIso() };
      dispatch({ type: 'share/record', record });
      call(api.recordShare(record));
    },

    setPrivacy(key: keyof Privacy, value: boolean) {
      dispatch({ type: 'profile/privacy', key, value });
      call(api.updateProfile(d().me, { privacy: { [key]: value } }));
    },

    /**
     * The Edit profile check action: save only the sections that changed (introduction, UNIT
     * affiliations, UNIT ID & referral). Api mode waits for the server, so a rejected value is
     * reported against its field and nothing local changes; '' clears the UNIT ID or link.
     * With affiliations, `primary` is one of them; an empty list clears them (no primary).
     */
    async saveProfile(edit: ProfileEdit): Promise<SaveResult> {
      const affiliations = edit.affiliations && {
        list: edit.affiliations.list,
        primary: edit.affiliations.list.includes(edit.affiliations.primary) ? edit.affiliations.primary : (edit.affiliations.list[0] ?? ''),
      };
      const unit = edit.unit && { unitId: edit.unit.unitId.trim(), referralUrl: edit.unit.referralUrl.trim() };
      const patch = { ...(edit.bio !== undefined ? { bio: edit.bio.trim() } : {}), ...(unit ?? {}) };
      if (!apiActive) {
        const me = d().me;
        if (patch.bio !== undefined) dispatch({ type: 'profile/bio', personId: me, bio: patch.bio });
        if (unit) dispatch({ type: 'profile/unitReferral', personId: me, ...unit });
        if (affiliations) dispatch({ type: 'profile/affiliations', personId: me, affiliations: affiliations.list, primary: affiliations.primary });
        toast('Profile saved');
        return { ok: true };
      }
      try {
        // One PATCH for the text fields (one transaction), then the affiliations.
        if (Object.keys(patch).length) await api.updateProfile(d().me, patch);
        if (affiliations) await api.setAffiliations(affiliations.list, affiliations.primary);
        await reload();
        toast('Profile saved');
        return { ok: true };
      } catch (err) {
        await reload(); // show exactly what the server kept
        if (err instanceof ApiError) return { ok: false, field: err.field, code: err.code };
        return { ok: false, code: 'network' };
      }
    },

    setHomeTheme(theme: HomeTheme) {
      dispatch({ type: 'profile/theme', theme });
      call(api.updateProfile(d().me, { homeTheme: theme }));
    },

    /** Upload or replace a photo, then reload so every screen gets the new server URL. Resolves true on success. */
    async savePhoto(kind: PhotoKind, image: Blob): Promise<boolean> {
      if (!apiActive) {
        toast('Photos are saved once you sign in with Google.');
        return false;
      }
      try {
        await api.uploadPhoto(kind, image);
        await reload();
        toast(kind === 'avatar' ? 'Profile photo saved' : kind === 'cover' ? 'Cover photo saved' : 'Background saved');
        return true;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return false;
        toast(err instanceof ApiError && (err.status === 413 || err.status === 415 || err.status === 422) ? 'That image can’t be used. Try a JPG, PNG or WebP photo.' : 'Could not save. Nothing was changed.');
        return false;
      }
    },

    async removePhoto(kind: PhotoKind): Promise<boolean> {
      if (!apiActive) return false;
      try {
        await api.removePhoto(kind);
        await reload();
        toast('Photo removed');
        return true;
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) toast('Could not save. Nothing was changed.');
        return false;
      }
    },

    resetDemo() {
      if (!isDemo) return; // never replace server data with the seed
      dispatch({ type: 'demo/reset', data: createSeed() });
      toast('Demo data reset');
    },
  };
}

export type Actions = ReturnType<typeof createActions>;
